import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { KeyRound, Loader2, ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ROLE_LOGIN, formatApiErrorDetail } from "../lib/authApi";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, valid: false });
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/api/auth/reset-password/validate`, { params: { token } }).then((r) => setState({ loading: false, ...r.data })).catch(() => setState({ loading: false, valid: false }));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 8) return setError("Use at least 8 characters.");
    if (pw !== pw2) return setError("Passwords do not match.");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/auth/reset-password`, { token, password: pw });
      navigate(ROLE_LOGIN[data.role] || "/portal/login", { replace: true, state: { notice: "Password set. Please sign in." } });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen pt-28 pb-16 px-4 flex items-start justify-center" data-testid="reset-password-page">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 space-y-6">
        <div className="flex items-center gap-3">
          <span className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center"><KeyRound className="h-5 w-5 text-primary" strokeWidth={1.5} /></span>
          <div><h1 className="text-xl font-heading font-semibold">{state.kind === "invite" ? "Set your password" : "Reset your password"}</h1><p className="text-sm text-muted-foreground">VECTRON GLOBAL account</p></div>
        </div>
        {state.loading ? <div className="py-8 flex justify-center" data-testid="reset-loading"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div> : !state.valid ? (
          <div className="space-y-4" data-testid="reset-invalid">
            <p className="text-sm">This link is invalid or has expired. Ask your VECTRON administrator to issue a new one.</p>
            <Button asChild variant="outline" className="w-full"><Link to="/portal/login" data-testid="reset-back-login">Back to sign in</Link></Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
            <p className="text-sm text-muted-foreground flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-primary" strokeWidth={1.5} />For <span className="font-medium" data-testid="reset-email-hint">{state.email_hint}</span> · one-time link</p>
            <div className="space-y-1"><Label htmlFor="pw">New password</Label><Input id="pw" type="password" autoComplete="new-password" minLength={8} required value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password-input" /></div>
            <div className="space-y-1"><Label htmlFor="pw2">Confirm password</Label><Input id="pw2" type="password" autoComplete="new-password" minLength={8} required value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="reset-password-confirm" /></div>
            {error && <p className="text-sm text-destructive" data-testid="reset-error">{error}</p>}
            <Button type="submit" className="w-full" disabled={busy} data-testid="reset-submit-btn">{busy ? "Saving…" : state.kind === "invite" ? "Set password & activate" : "Save new password"}</Button>
          </form>
        )}
      </div>
    </div>
  );
}
